/**
 * Hook for fetching and managing RFP data for the Send drawer
 */

import { toMinutes, useApi } from "@platform/api";
import {
  useRfpBrokerage,
  useRfpCarriers,
  useRfpClientDetails,
} from "@platform/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";

import { useCoverageOverview } from "../../../hooks/useCoverageOverview";
import {
  transformBrokerageResponse,
  transformCarriersResponse,
  transformClientDetailsResponse,
  useRfpWorkbenchSend,
  useSendRfpCoverages,
  type SendRfpBrokerageDetails,
  type SendRfpCarrier,
  type SendRfpClientDetails,
  type SendRfpCoverage,
  type SendRfpSubmissionData,
} from "../api";
import { EditedReviewFields } from "../ReviewSection";

import type { CoverageOverviewItem } from "../../../types";

interface UseSendRfpDataParams {
  ecoRfpId: string;
  brokerPortalRfpId: string;
}

interface WorkbenchBuildResponse {
  analystDocsFolderUrl?: string;
}

interface UseSendRfpDataResult {
  // Data
  carriers: SendRfpCarrier[];
  coveragesWithCompletion: SendRfpCoverage[];
  clientDetails: SendRfpClientDetails | undefined;
  brokerageDetails: SendRfpBrokerageDetails | undefined;
  submissionData: SendRfpSubmissionData | undefined;
  analystDocsFolderUrl: string | undefined;

  // Loading states
  isLoading: boolean;
  isError: boolean;

  // Initial coverage selection
  getInitialCoverageIds: () => string[];
  markCoveragesInitialized: () => void;
  areCoveragesInitialized: () => boolean;
}

/**
 * Fetches all RFP data needed for the Send drawer
 */
export function useSendRfpData({
  ecoRfpId,
  brokerPortalRfpId,
}: UseSendRfpDataParams): UseSendRfpDataResult {
  // Track if we've initialized the selected coverages
  const initializedCoverages = useRef(false);
  const axiosInstance = useApi();

  // Fetch carriers using shared hook with transformation
  const {
    data: carriers = [],
    isLoading: isLoadingCarriers,
    isError: isCarriersError,
  } = useRfpCarriers({
    rfpId: brokerPortalRfpId,
    enabled: !!brokerPortalRfpId,
    select: transformCarriersResponse,
  });

  // Fetch coverages
  const {
    data: coverages = [],
    isLoading: isLoadingCoverages,
    isError: isCoveragesError,
  } = useSendRfpCoverages({ ecoRfpId, enabled: !!ecoRfpId });

  // Fetch client details using shared hook with transformation
  const {
    data: clientDetails,
    isLoading: isLoadingClientDetails,
    isError: isClientDetailsError,
  } = useRfpClientDetails({
    rfpId: brokerPortalRfpId,
    enabled: !!brokerPortalRfpId,
    select: transformClientDetailsResponse,
  });

  // Fetch brokerage details
  const {
    data: brokerageDetails,
    isLoading: isLoadingBrokerage,
    isError: isBrokerageError,
  } = useRfpBrokerage({
    rfpId: brokerPortalRfpId,
    enabled: !!brokerPortalRfpId,
    select: transformBrokerageResponse,
  });

  // Fetch submission data
  const {
    data: submissionData,
    isLoading: isLoadingSubmissionData,
    isError: isSubmissionDataError,
  } = useRfpWorkbenchSend({
    rfpId: brokerPortalRfpId,
    enabled: !!brokerPortalRfpId,
  });

  // Fetch analyst documents folder URL from workbench/build endpoint
  const { data: buildData } = useQuery<WorkbenchBuildResponse>({
    queryKey: ["rfp", "workbench", "build", brokerPortalRfpId],
    queryFn: () =>
      axiosInstance
        .get<WorkbenchBuildResponse>(
          `brokerportal/v1/rfp/${brokerPortalRfpId}/workbench/build`,
        )
        .then((res) => res.data),
    enabled: !!brokerPortalRfpId,
    staleTime: toMinutes(5),
  });

  // Fetch coverage completion status from coverage summary. Pass the app's
  // configured axios instance — the hook defaults to a bare axios with no
  // baseURL, so the relative request resolves against the page URL and the SPA
  // fallback returns index.html instead of the summary JSON.
  const { coverageQuery } = useCoverageOverview({ ecoRfpId, axiosInstance });
  const coverageSummary = coverageQuery.data;

  // Build a map of coverage type enum → isComplete from coverage summary.
  // Used as a fallback for coverages that have no per-plan summary rows.
  const coverageCompletionMap = useMemo(() => {
    const map = new Map<string, boolean>();
    if (coverageSummary) {
      const allCoverages = [
        ...(coverageSummary.existingCoverages ?? []),
        ...(coverageSummary.newCoverages ?? []),
      ];
      allCoverages.forEach((cov) => {
        map.set(cov.type, cov.isComplete);
      });
    }
    return map;
  }, [coverageSummary]);

  // Group the summary's per-plan rows by coverage id. The summary has one row
  // per benefit plan, so a coverage with multiple plans (e.g. Dental PPO +
  // Dental HMO) contributes several rows under the same coverage id. The BTX
  // /coverages endpoint has no per-plan label, so these rows are the source of
  // the plan-formatted names and per-plan completion.
  const coveragePlanRowsMap = useMemo(() => {
    const map = new Map<string, CoverageOverviewItem[]>();
    if (coverageSummary) {
      const allCoverages = [
        ...(coverageSummary.existingCoverages ?? []),
        ...(coverageSummary.newCoverages ?? []),
      ];
      allCoverages.forEach((cov) => {
        const rows = map.get(cov.id) ?? [];
        rows.push(cov);
        map.set(cov.id, rows);
      });
    }
    return map;
  }, [coverageSummary]);

  // Expand each BTX coverage into one selectable chip per benefit plan. A
  // multi-plan coverage (Dental PPO/HMO) yields one chip per plan, each keyed
  // by its note key so it selects independently, yet all share the same
  // coverage id and type — the coverage stays a single unit at submission time.
  // Coverages with no summary rows fall back to a single chip.
  const coveragesWithCompletion: SendRfpCoverage[] = useMemo(
    () =>
      coverages.flatMap((cov) => {
        const planRows = coveragePlanRowsMap.get(cov.id);

        if (!planRows?.length) {
          return [
            {
              ...cov,
              isComplete: coverageCompletionMap.get(cov.type) ?? false,
            },
          ];
        }

        return planRows.map((row) => {
          const benefitPlanType =
            row.productType && row.productType !== "notApplicable"
              ? row.productType
              : undefined;
          return {
            ...cov,
            id: benefitPlanType ? `${cov.id}:${benefitPlanType}` : cov.id,
            benefitPlanType,
            label: row.name,
            isComplete: row.isComplete,
          };
        });
      }),
    [coverages, coverageCompletionMap, coveragePlanRowsMap],
  );

  // Combined loading state. Includes coverageQuery so the initial coverage
  // selection (isComplete, derived from this summary) isn't computed before
  // the summary arrives — otherwise every coverage looks incomplete and the
  // one-time auto-select effect locks in an empty selection.
  const isLoading =
    isLoadingCarriers ||
    isLoadingCoverages ||
    isLoadingClientDetails ||
    isLoadingBrokerage ||
    isLoadingSubmissionData ||
    coverageQuery.isLoading;

  const isError =
    isCarriersError ||
    isCoveragesError ||
    isClientDetailsError ||
    isBrokerageError ||
    isSubmissionDataError;

  // Helper to get initial complete coverage IDs
  const getInitialCoverageIds = (): string[] =>
    coveragesWithCompletion
      .filter((cov) => cov.isComplete === true)
      .map((cov) => cov.id);

  const markCoveragesInitialized = (): void => {
    initializedCoverages.current = true;
  };

  const areCoveragesInitialized = (): boolean => initializedCoverages.current;

  return {
    carriers,
    coveragesWithCompletion,
    clientDetails,
    brokerageDetails,
    submissionData,
    analystDocsFolderUrl: buildData?.analystDocsFolderUrl,
    isLoading,
    isError,
    getInitialCoverageIds,
    markCoveragesInitialized,
    areCoveragesInitialized,
  };
}

/**
 * Validates that all required fields are present for RFP submission
 * Uses edited fields from ReviewSection when available, falls back to API data
 */
export function validateSubmissionData(
  submissionData: SendRfpSubmissionData | undefined,
  clientDetails: SendRfpClientDetails | undefined,
  brokerageDetails: SendRfpBrokerageDetails | undefined,
  editedFields: EditedReviewFields | null,
): { isValid: boolean; missingFields: string[] } {
  // Required fields with getters that resolve edited values over API data
  const requiredFields: {
    label: string;
    getValue: () => string | null | undefined;
  }[] = [
    { label: "Effective Date", getValue: () => submissionData?.effectiveDate },
    {
      label: "Carrier Due Date",
      getValue: () =>
        editedFields?.rfp.carrierDueDate ?? submissionData?.carrierDueDate,
    },
    {
      label: "Client Name",
      getValue: () => editedFields?.client.name ?? clientDetails?.name,
    },
    {
      label: "Client Address",
      getValue: () =>
        editedFields?.client.addressStreet ??
        clientDetails?.address?.addressLine1,
    },
    {
      label: "Client City",
      getValue: () =>
        editedFields?.client.addressCity ?? clientDetails?.address?.city,
    },
    {
      label: "Client State",
      getValue: () =>
        editedFields?.client.addressState ?? clientDetails?.address?.state,
    },
    {
      label: "Client Zipcode",
      getValue: () =>
        editedFields?.client.addressZipcode ?? clientDetails?.address?.zipCode,
    },
    {
      label: "Situs State",
      getValue: () =>
        editedFields?.client.situsState ?? clientDetails?.situsState,
    },
    {
      label: "Eligible Employees",
      getValue: () =>
        editedFields?.client.numberOfEmployees ??
        clientDetails?.numberOfEmployees?.toString(),
    },
    {
      label: "Broker Address",
      getValue: () =>
        editedFields?.brokerage.addressStreet ??
        brokerageDetails?.address?.address,
    },
    {
      label: "Broker City",
      getValue: () =>
        editedFields?.brokerage.addressCity ?? brokerageDetails?.address?.city,
    },
    {
      label: "Broker State",
      getValue: () =>
        editedFields?.brokerage.addressState ??
        brokerageDetails?.address?.state,
    },
    {
      label: "Broker Zipcode",
      getValue: () =>
        editedFields?.brokerage.addressZipcode ??
        brokerageDetails?.address?.zipcode,
    },
    {
      label: "Producer First Name",
      getValue: () =>
        editedFields?.brokerage.producerFirstName ??
        brokerageDetails?.producer?.firstName ??
        undefined,
    },
    {
      label: "Producer Last Name",
      getValue: () =>
        editedFields?.brokerage.producerLastName ??
        brokerageDetails?.producer?.lastName ??
        undefined,
    },
    {
      label: "Producer Email",
      getValue: () =>
        editedFields?.brokerage.producerEmail ??
        brokerageDetails?.producer?.email,
    },
  ];

  const missingFields = requiredFields
    .filter(({ getValue }) => !getValue())
    .map(({ label }) => label);

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

interface CarrierDerivedState {
  carriersWithMissingSalesRep: SendRfpCarrier[];
  showCarrierWarning: boolean;
  selectedCarriersWithMissingSalesRep: SendRfpCarrier[];
  selectedUnconnectedCarriers: SendRfpCarrier[];
  selectedConnectedCarriers: SendRfpCarrier[];
}

/**
 * Computes derived state for carriers
 */
export function useCarrierDerivedState(
  carriers: SendRfpCarrier[],
  selectedCarriers: string[],
): CarrierDerivedState {
  // Calculate carriers missing sales rep emails (connected and unconnected)
  const carriersWithMissingSalesRep = useMemo(
    () => carriers.filter((carrier) => !carrier.salesRepEmail),
    [carriers],
  );

  const showCarrierWarning = carriersWithMissingSalesRep.length > 0;

  // Block submission only if selected unconnected carriers are missing sales rep emails
  const selectedCarriersWithMissingSalesRep = useMemo(
    () =>
      carriersWithMissingSalesRep.filter((carrier) =>
        selectedCarriers.includes(carrier.id),
      ),
    [carriersWithMissingSalesRep, selectedCarriers],
  );

  // Get selected unconnected carriers (for email draft)
  const selectedUnconnectedCarriers = useMemo(
    () =>
      carriers.filter(
        (carrier) =>
          carrier.status === "unconnected" &&
          selectedCarriers.includes(carrier.id),
      ),
    [carriers, selectedCarriers],
  );

  // Get selected connected carriers (for offline email draft)
  const selectedConnectedCarriers = useMemo(
    () =>
      carriers.filter(
        (carrier) =>
          carrier.status === "connected" &&
          selectedCarriers.includes(carrier.id),
      ),
    [carriers, selectedCarriers],
  );

  return {
    carriersWithMissingSalesRep,
    showCarrierWarning,
    selectedCarriersWithMissingSalesRep,
    selectedUnconnectedCarriers,
    selectedConnectedCarriers,
  };
}
